import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { TableCell, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useSyncMember, useCancelMember } from '@/hooks'
import type { MemberWithWallet } from '@/lib/api'
import { MEMBER_STATUS_LABELS, formatCurrency, formatRelativeDate } from '@/lib/constants'
import { Copy, Pencil, UserX } from 'lucide-react'
import { toast } from 'sonner'
import { IntegrationStatus } from './integration-status'

interface MemberRowProps {
  member: MemberWithWallet
  onEdit: (member: MemberWithWallet) => void
}

export function MemberRow({ member, onEdit }: MemberRowProps) {
  const { mutate: syncMember, isPending: isSyncing } = useSyncMember()
  const { mutate: cancelMember, isPending: isCancelling } = useCancelMember()
  const [showCancelDialog, setShowCancelDialog] = useState(false)

  return (
    <>
      <TableRow>
        <TableCell className="text-muted-foreground text-sm">
          {formatRelativeDate(member.createdAt)}
        </TableCell>
        <TableCell>
          <div>
            <div className="font-medium">{member.name}</div>
            <div className="text-sm text-muted-foreground">{member.phone || '-'}</div>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{member.referralCode}</Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={() => {
                navigator.clipboard.writeText(member.referralCode)
                toast.success('Código copiado!')
              }}
            >
              <Copy className="h-3 w-3" />
              <span className="sr-only">Copiar código</span>
            </Button>
          </div>
        </TableCell>
        <TableCell>
          <div>
            <div className="font-medium text-green-600">
              {formatCurrency(member.wallet?.available ?? 0)}
            </div>
            {(member.wallet?.pending ?? 0) > 0 && (
              <div className="text-xs text-yellow-600">
                {formatCurrency(member.wallet.pending)} travado
              </div>
            )}
            {(member.wallet?.totalRedeemed ?? 0) > 0 && (
              <div className="text-xs text-muted-foreground">
                {formatCurrency(member.wallet.totalRedeemed)} sacado
              </div>
            )}
          </div>
        </TableCell>
        <TableCell className="hidden md:table-cell text-muted-foreground">{member.email}</TableCell>
        <TableCell>
          <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
            {MEMBER_STATUS_LABELS[member.status]}
          </Badge>
        </TableCell>
        <TableCell className="hidden sm:table-cell">
          <div className="flex items-center gap-1">
            <IntegrationStatus
              name="Guru"
              couponId={member.guruCouponId}
              isSyncing={isSyncing}
              onSync={() => syncMember(member.id)}
            />
            <IntegrationStatus
              name="Yampi"
              couponId={member.yampiCouponId}
              isSyncing={isSyncing}
              onSync={() => syncMember(member.id)}
            />
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => onEdit(member)}>
              <Pencil className="h-4 w-4" />
            </Button>
            {member.status === 'active' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setShowCancelDialog(true)}
                      disabled={isCancelling}
                    >
                      <UserX className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Desativar membro</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </TableCell>
      </TableRow>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar membro?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso irá desativar o membro <strong>{member.name}</strong> e seus cupons nas
              integrações (Guru/Yampi). O membro não poderá mais gerar novas indicações.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                cancelMember(member.id)
                setShowCancelDialog(false)
              }}
            >
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
